import React, { useState, useEffect } from 'react'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb'
import {
  MotifButton,
  MotifProgressIndicator,
  MotifProgressIndicatorStep,
  MotifToast,
  MotifTooltip,
} from '@ey-xd/motif-react'
import DeveloperEvidence from './DeveloperEvidence/DeveloperEvidence'
import Appropriateness from './Appropriateness/Appropriateness'
import { progressData } from './Constant'
import './ApplicationDetails.scss' // Example icon import
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useLocation } from 'react-router-dom'
import {
  ApplicationDetail,
  ApplicationList,
  EngagementDetail,
  EvidenceStatusEnum,
} from '@/services/types'
import SODResult from '../SODResult/SODResult'
import { clearSodId, setSodId } from '@/features/sodAnalysis/SodSlice'
import {
  useCreateSodAnalysisMutation,
  useGetSodAnalysisStatusQuery,
  useGetSodSelectedIdentifiersQuery,
} from '@/services/sodanalysis/sodReducer'
import SodReview from '../SODResult/SodReview'
import { useListEvidenceQuery } from '@/services/application/evidenceReducer'

export default function ApplicationDetails() {
  const dispatch = useDispatch()

  const [currentProgress, setCurrentProgress] = useState<
    {
      id: string
      body: string
      heading: string
      status: 'default' | 'active' | 'completed' | 'warning' | undefined
    }[]
  >(() => progressData.map((item) => ({ ...item, status: 'default' })))
  const [stage, setStage] = useState('developerDetails')
  const [completedStage, SetCompletedStage] = useState<string[]>([])
  const currentEngagement = useSelector(
    (state: { clientCard: { currentEngagement: EngagementDetail } }) =>
      state.clientCard.currentEngagement,
  )
  const [canMarkAnalysisComplete, setCanMarkAnalysisComplete] = useState(false)
  const currentApplications = useSelector(
    (state: { clientCard: { applicationList: ApplicationList } }) =>
      state.clientCard.applicationList,
  )
  const { applicationId } = useParams<{ applicationId: string }>()
  const location = useLocation()
  const [toast, setToast] = useState<{ show: boolean; message: string }>({
    show: false,
    message: '',
  })
  const applicationName: string =
    (location.state as { applicationName?: string })?.applicationName ??
    (Array.isArray(currentApplications)
      ? currentApplications.find((obj: ApplicationDetail) => obj.id === applicationId)?.applicationName
      : undefined) ??
    ''
  const sodId = useSelector(
    (state: { sod: { sodId: string | null } }) => state.sod.sodId,
  )

  const [createSodAnalysis, { isLoading: isCreating }] =
    useCreateSodAnalysisMutation()

  const { data: sodAnalysisData } =
    useGetSodAnalysisStatusQuery(applicationId ?? '')

  useEffect(() => {
    if (sodAnalysisData &&
      sodAnalysisData.isCompleted &&
      sodAnalysisData.hasDeveloperEvidence &&
      sodAnalysisData.hasImplementerEvidence) {
      dispatch(setSodId(sodAnalysisData.applicationId))
    }
  }, [sodAnalysisData, dispatch])

  useEffect(() => {
    dispatch(clearSodId())
  }, [applicationId, dispatch])

  useEffect(() => {
    if (sodAnalysisData?.isCompleted) {
      SetCompletedStage(progressData.map((s) => s.id))
      setStage('Results')
    }
  }, [sodAnalysisData?.isCompleted])

  const breadcrumbItems = [
    {
      text: 'Home',
      url: '/',
      blockNavigation: false,
    },
    {
      text: currentEngagement.engagementName,
      url: `/${currentEngagement.engagementID}?showDev=true`,
      disabled: false,
    },
    {
      text: applicationName,
      disabled: true,
    },
  ]

  useEffect(() => {
    const updatedProgress: {
      id: string
      body: string
      heading: string
      status: 'default' | 'active' | 'completed' | 'warning' | undefined
    }[] = currentProgress.map((item) => {
      if (completedStage.includes(item.id)) {
        return { ...item, status: 'completed' }
      } else if (item.id === stage) {
        return { ...item, status: 'active' }
      } else {
        return { ...item, status: 'default' }
      }
    })
    setCurrentProgress(updatedProgress)
  }, [stage, completedStage])

  function handleNext() {
    if (stage === 'developerDetails') {
      SetCompletedStage([...completedStage, 'developerDetails'])
      setStage('devAppropriateness')
    } else if (stage === 'devAppropriateness') {
      SetCompletedStage([...completedStage, 'devAppropriateness'])
      setStage('implementerDetails')
    } else if (stage === 'implementerDetails') {
      SetCompletedStage([...completedStage, 'implementerDetails'])
      setStage('impAppropriateness')
    } else if (stage === 'impAppropriateness') {
      SetCompletedStage([...completedStage, 'impAppropriateness'])
      setStage('SODAnalysis')
    } else if (stage === 'SODAnalysis') {
      SetCompletedStage([...completedStage, 'SODAnalysis'])
      setStage('Results')
    }
  }

  const handleClose = () => {
    setToast({
      show: false,
      message: '',
    })
  }

  useEffect(() => {
    if (toast.show) {
      setTimeout(() => {
        setToast({
          show: false,
          message: '',
        })
      }, 6000)
    }
  }, [toast.show])

  async function handleResults() {
    try {
      const response = await createSodAnalysis(applicationId).unwrap()
      dispatch(setSodId(response))
      console.log('Generated SOD ID:', response.sodId)
      SetCompletedStage(progressData.map((s) => s.id))
    } catch (error) {
      let errorMessage = 'Error downloading file. Please try again.'
      if (error?.data?.message) {
        errorMessage = error.data.message
      } else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      setToast({
        show: true,
        message: errorMessage,
      })
      console.error('Error generating SOD ID:', error)
    }
  }

  function handleBack() {
    if (stage === 'devAppropriateness') {
      setStage('developerDetails')
    } else if (stage === 'implementerDetails') {
      setStage('devAppropriateness')
    } else if (stage === 'impAppropriateness') {
      setStage('implementerDetails')
    } else if (stage === 'SODAnalysis') {
      setStage('impAppropriateness')
    } else if (stage === 'Results') {
      setStage('SODAnalysis')
    }
  }

  const { data: sodSelectedIdentifiersData } = useGetSodSelectedIdentifiersQuery(applicationId ?? '')

  const { data: developerEvidenceList } = useListEvidenceQuery(
    { applicationId, evidenceType: 1 },
    { skip: !applicationId },
  )

  const { data: implementerEvidenceList } = useListEvidenceQuery(
    { applicationId, evidenceType: 2 },
    { skip: !applicationId },
  )

  const isEvidenceAnnotationVerified = (list: any[] | undefined) =>
    Array.isArray(list) &&
    list.length > 0 &&
    list.every(
      (item: any) =>
        item?.evidenceStatus === EvidenceStatusEnum.Verified &&
        item?.supplementRequired !== true,
    )

  const allEvidenceAnnotationVerified =
    isEvidenceAnnotationVerified(developerEvidenceList) &&
    isEvidenceAnnotationVerified(implementerEvidenceList)

  const handleClick = (id: string) => {
    // Handle click event on navigation
    if (id === 'SODAnalysis' && sodSelectedIdentifiersData.developerIdentifiers.length > 0 && sodSelectedIdentifiersData.implementerIdentifiers.length > 0) {
      setStage('Results')
    } else {
      setStage(id)
    }
  }

  return (
    <div className="motif-container no-gutters">
      <div className="motif-col-lg-12  main-container">
        <Breadcrumb items={breadcrumbItems} ariaLabel="Breadcrumb navigation" />
        <div className="application-details-subheader">
          Developer/Implementer Input Files
        </div>
        <div className="application-details-wrapper">
          <MotifButton
            className="motif-button"
            size="small"
            variant="primary"
            onClick={handleBack}
            disabled={stage === 'developerDetails'}
          >
            Previous
          </MotifButton>
          <MotifProgressIndicator
            className="application-details-stepper"
            size="sm"
            variant="row"
          >
            {currentProgress.map((item, index) => (
              <MotifProgressIndicatorStep
                key={index}
                body={item.body}
                heading={item.heading}
                status={item.status}
                onClick={() => {
                  handleClick(item.id)
                }}
                className={item.status === 'completed' ? 'completed-step' : ''}
                name={''}
              >
                {item.status !== 'completed' && <span className="step-dot-icon" />}
              </MotifProgressIndicatorStep>
            ))}
          </MotifProgressIndicator>
          <MotifTooltip
            closeButtonProps={{}}
            flip
            forceHideCounter={0}
            id="tooltip"
            offset={[0, 8]}
            placement="top"
            hide={stage !== 'devAppropriateness'}
            trigger={
              <MotifButton
                className="motif-button"
                size="small"
                variant="primary"
                onClick={stage === 'Results' ? handleResults : handleNext}
                disabled={
                  (isCreating && stage === 'SODAnalysis') ||
                  (!!sodId && stage === 'SODAnalysis') ||
                  (stage === 'SODAnalysis' && !canMarkAnalysisComplete)
                }
              >
                {stage === 'Results'
                  ? isCreating
                    ? 'Generating...'
                    : !sodId ?
                      'Mark Analysis Complete'
                      : 'Analysis Complete'
                  : 'Next'}
              </MotifButton>
            }
            usePortal
            variant="default"
          >
            <div className="motif-caption-default-light motif-tooltip-custom-width">
              If implementation is automated for your application, you can click
              Next to skip the implementer evidence and appropriateness and
              proceed to generate your workbook for developer appropriateness
              from the Results page
            </div>
          </MotifTooltip>
        </div>
        {toast.show && (
          <MotifToast
            // @ts-ignore
            open={toast.show}
            onClose={() => handleClose()}
            variant="error"
          >
            {toast.message}
          </MotifToast>
        )}
        <div className="application-details-content">
          {stage === 'developerDetails' && (
            <DeveloperEvidence evidenceType={1} />
          )}
          {stage === 'devAppropriateness' && (
            <Appropriateness
              evidenceType={1}
              onNavigateToEvidence={() => setStage('developerDetails')}
            />
          )}
          {stage === 'implementerDetails' && (
            <DeveloperEvidence evidenceType={2} />
          )}
          {stage === 'impAppropriateness' && (
            <Appropriateness
              evidenceType={2}
              onNavigateToEvidence={() => setStage('implementerDetails')}
            />
          )}
          {stage === 'SODAnalysis' && (
            <SodReview onContinue={() => setStage('Results')} developerIdentifiers={sodSelectedIdentifiersData.developerIdentifiers} implementerIdentifiers={sodSelectedIdentifiersData.implementerIdentifiers} />
          )}
          {stage === 'Results' && (
            <SODResult
              setCanMarkAnalysisComplete={setCanMarkAnalysisComplete}
            />
          )}
        </div>
      </div>
    </div>
  )
}
